import React, { useMemo, useEffect, useCallback, useRef } from 'react'
import { graphql, useFragment } from 'react-relay'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'

import { useFormikContext } from 'formik'

import { XStack, YStack, Label } from 'tamagui'
import { useMutation } from '@data/useMutation'
import { useFlashMessage } from '@newComponents/DynamicIsland/useFlashMessage'
import { Form } from '@newComponents/Form/Form'
import { Paragraph, Strong, Text } from '@newComponents/Text'
import { Button } from '@newComponents/Button/Button'
import { Link } from '@newComponents/Link'
import { Spinner } from '@newComponents/Spinner'
import { Icon } from '@newComponents/Icon'

import { EventTicketingTierItem } from '@newComponents/EventTicketingTier/EventTicketingTierItem'

import {
  EventAttendContent_event$key,
  EventAttendContent_event$data,
  EventAttendeeStatus,
} from './__generated__/EventAttendContent_event.graphql'
import { EventAttendContentMutation } from './__generated__/EventAttendContentMutation.graphql'

import { EventAttendContent_query$key } from './__generated__/EventAttendContent_query.graphql'

export const EventAttendContent = ({
  event,
  query,
}: {
  event: EventAttendContent_event$key
  query: EventAttendContent_query$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventAttendContent_event on Event {
        rowId

        name
        prettyUrl

        hasVenue
        hasExternalUrl

        acceptingAttendance

        owner {
          __typename

          ... on User {
            firstName
            lastName
          }

          ... on Guild {
            name

            myMembership {
              id
            }
          }
        }

        myAttendance {
          status

          ticketPurchase {
            status
          }
        }

        eventTicketingTiers {
          edges {
            node {
              id

              ...EventTicketingTierItem_eventTicketingTier
            }

            cursor
          }
        }
      }
    `,
    event
  )

  const queryData = useFragment(
    graphql`
      fragment EventAttendContent_query on Query {
        viewer {
          id
          slugId
        }
      }
    `,
    query
  )

  const navigate = useNavigate()
  const location = useLocation()
  const { showFlash } = useFlashMessage()

  const [searchParams] = useSearchParams()
  const status: EventAttendeeStatus =
    (searchParams.get('status') as EventAttendeeStatus | null) ||
    (data.hasVenue ? 'ATTENDING_IN_PERSON' : 'ATTENDING_VIRTUAL')

  const discountCode = searchParams.get('discount')

  const confirmedSearchParam = searchParams.get('confirmed')

  const needsConfirmation = useMemo(() => {
    if (confirmedSearchParam === 'true') {
      return false
    }

    if (data.eventTicketingTiers.edges.length) {
      return true
    }

    if (data.owner?.__typename === 'Guild') {
      return data.owner.myMembership === null
    }

    return true
  }, [data, confirmedSearchParam])

  const attendEventMutation = useMutation<EventAttendContentMutation>(
    graphql`
      mutation EventAttendContentMutation($input: AttendEventInput!) {
        attendEvent(input: $input) {
          event {
            ...EventCard_event

            ...EventEditAttendeesList_event
            ...EventAttendButtonNew_event
            ...EventItemNew_event
            ...EventAttendContent_event

            ...EventScreenNewMobile_event
            ...EventScreenNewDesktop_event
            ...EventScreenAttendeesDialogContent_event
          }
        }
      }
    `
  )

  const isSubmitting = useRef(false)

  const attendEventAction = useCallback(async () => {
    if (!isSubmitting.current) {
      try {
        isSubmitting.current = true

        if (!queryData.viewer) {
          const authRedirectPath = discountCode
            ? `/events/${data.prettyUrl}/attend?status=${status}&confirmed=true&discount=${discountCode}`
            : `/events/${data.prettyUrl}/attend?status=${status}&confirmed=true`

          navigate('/sign-up', {
            state: {
              backgroundLocation:
                location.state?.backgroundLocation || location,
              // authRedirectPath: data.eventTicketingTiers.edges.length
              //   ? `/events/${data.prettyUrl}/payment`
              //   : `/events/${data.prettyUrl}/attend?status=${status}`,
              authRedirectPath,
            },
          })
          return
        }

        await attendEventMutation({
          variables: { input: { eventId: data.rowId, status } },
        })

        const flashMessage =
          status === 'NOT_ATTENDING'
            ? "You're no longer attending"
            : status.startsWith('WAITING')
              ? "You're on the waitlist"
              : "You're attending!"

        showFlash(flashMessage, 'success')
      } catch (err) {
        showFlash('Failed to update attendance', 'error')
      } finally {
        isSubmitting.current = false
      }
    }
  }, [attendEventMutation, data, navigate, location, showFlash])

  const name = useMemo(() => {
    if (!data.owner) {
      return null
    }
    if (data.owner.__typename === 'Guild') {
      return data.owner.name
    } else if (data.owner.__typename === 'User') {
      return `${data.owner.firstName} ${data.owner.lastName}`
    }
  }, [data.owner])

  useEffect(() => {
    if (!data.myAttendance && !needsConfirmation) {
      attendEventAction()
      return
    }

    if (
      ['WAITING_IN_PERSON', 'WAITING_VIRTUAL'].includes(
        data.myAttendance?.status || ''
      ) &&
      data.eventTicketingTiers.edges.length &&
      data.myAttendance?.ticketPurchase?.status !== 'SUCCEEDED' &&
      data.acceptingAttendance
    ) {
      if (!isSubmitting.current) {
        navigate(
          discountCode
            ? `/events/${data.prettyUrl}/payment?discount=${discountCode}`
            : `/events/${data.prettyUrl}/payment`,
          {
            state: {
              backgroundLocation:
                location.state?.backgroundLocation || location,
            },
          }
        )
        return
      }
    }

    if (
      [
        'ATTENDING_IN_PERSON',
        'ATTENDING_VIRTUAL',
        'WAITING_IN_PERSON',
        'WAITING_VIRTUAL',
      ].includes(data.myAttendance?.status || '')
    ) {
      const timeoutId = setTimeout(() => {
        navigate(`/events/${data.prettyUrl}`)
      }, 3000)

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [
    needsConfirmation,
    data.prettyUrl,
    data.myAttendance?.status,
    data.eventTicketingTiers.edges.length,
    // @ts-ignore stripePaymentIntentStatus may be in extended fragment
    data.myAttendance?.ticketPurchase?.stripePaymentIntentStatus,
  ])

  if (data.myAttendance?.status) {
    if (
      ['ATTENDING_IN_PERSON', 'ATTENDING_VIRTUAL'].includes(
        data.myAttendance.status
      )
    ) {
      return (
        <YStack gap='$4' alignItems='center'>
          <Paragraph>You're attending the event!</Paragraph>

          <YStack padding='$3' backgroundColor='$darkBlue9' borderRadius='$20'>
            <Icon size='$14' name='checkmark-filled' color='$grey0' />
          </YStack>

          <Paragraph size='$4' color='$grey6'>
            We're redirecting you to the event page now
          </Paragraph>
        </YStack>
      )
    }

    if (
      ['WAITING_IN_PERSON', 'WAITING_VIRTUAL'].includes(
        data.myAttendance.status
      )
    ) {
      if (
        data.eventTicketingTiers.edges.length &&
        data.myAttendance?.ticketPurchase?.status !== 'SUCCEEDED' &&
        data.acceptingAttendance
      ) {
        return (
          <YStack gap='$4' alignItems='center'>
            <YStack gap='$2' alignItems='center'>
              <Paragraph textAlign='center'>Awaiting Payment</Paragraph>
            </YStack>

            <Spinner size='$12' color='$darkBlue10' />

            <Paragraph size='$4' color='$grey6'>
              We're directing you to the{' '}
              <Link
                size='$4'
                color='$darkBlue7'
                href={`/events/${data.prettyUrl}/payment`}
                options={{
                  state: {
                    backgroundLocation:
                      location.state?.backgroundLocation || location,
                  },
                }}
              >
                Payment Screen
              </Link>{' '}
              now
            </Paragraph>
          </YStack>
        )
      }

      if (data.myAttendance.ticketPurchase?.status === 'SUCCEEDED') {
        return (
          <YStack gap='$4' alignItems='center'>
            <YStack gap='$2' alignItems='center'>
              <Paragraph textAlign='center'>
                You're waiting for a place to open up
              </Paragraph>
              <Paragraph textAlign='center'>
                We'll email you if you get a spot!
              </Paragraph>
            </YStack>

            <Spinner size='$12' color='$darkBlue10' />

            <Paragraph size='$4' color='$grey6'>
              We're redirecting you to the event page now
            </Paragraph>
          </YStack>
        )
      }

      return (
        <YStack gap='$4' alignItems='center'>
          <YStack gap='$2' alignItems='center'>
            <Paragraph textAlign='center'>
              Looks like there aren't any spots available at this time
            </Paragraph>
            <Paragraph textAlign='center'>
              For now, we've recorded your interest
            </Paragraph>
            <Paragraph textAlign='center'>
              Check back later if any become available!
            </Paragraph>
          </YStack>

          <Spinner size='$12' color='$darkBlue10' />

          <Paragraph size='$4' color='$grey6'>
            We're redirecting you to the event page now
          </Paragraph>
        </YStack>
      )
    }
  }

  if (!needsConfirmation || isSubmitting.current) {
    return (
      <YStack gap='$4' alignItems='center'>
        <Spinner size='$12' color='$darkBlue10' />

        <Paragraph size='$4' color='$grey6'>
          You'll be redirected automatically
        </Paragraph>
      </YStack>
    )
  }

  return (
    <YStack gap='$8'>
      {data.eventTicketingTiers.edges.map((edge) => {
        if (!edge.node) {
          return null
        }

        return (
          <EventTicketingTierItem
            key={edge.cursor}
            eventTicketingTier={edge.node}
          />
        )
      })}

      <YStack gap='$6' $gtXs={{ display: 'none' }}>
        <YStack gap='$2' flex={1} alignItems='center'>
          <YStack justifyContent='center'>
            <Button onPress={attendEventAction}>
              <Button.Text>
                {data.eventTicketingTiers.edges.length > 0
                  ? 'Yes, Proceed to Checkout'
                  : 'Sounds good'}
              </Button.Text>
            </Button>
          </YStack>

          {(data.owner?.__typename === 'Guild' && !data.owner.myMembership) ||
          data.owner?.__typename !== 'Guild' ? (
            <>
              <Paragraph
                size='$4'
                textAlign='center'
                color='$grey7'
                maxWidth={348}
              >
                By attending the event, you agree to share your information with{' '}
                {name}.
              </Paragraph>

              <Paragraph
                size='$4'
                textAlign='center'
                color='$grey7'
                maxWidth={348}
              >
                They may contact you about this event and other community
                initiatives.
              </Paragraph>

              <Paragraph
                size='$4'
                textAlign='center'
                color='$grey7'
                maxWidth={348}
              >
                Tune your notifications in your{' '}
                <Link
                  color='$darkBlue7'
                  size='$4'
                  href={`/${queryData.viewer?.slugId}/settings/notifications`}
                  target='_blank'
                >
                  Settings
                </Link>
                .
              </Paragraph>
            </>
          ) : null}
        </YStack>
      </YStack>

      <XStack gap='$6' display='none' $gtXs={{ display: 'flex' }}>
        <YStack gap='$2' flex={1} alignItems='flex-end'>
          {(data.owner?.__typename === 'Guild' && !data.owner.myMembership) ||
          data.owner?.__typename !== 'Guild' ? (
            <>
              <Paragraph
                size='$4'
                textAlign='right'
                color='$grey7'
                maxWidth={348}
              >
                By attending the event, you agree to share your information with{' '}
                {name}.
              </Paragraph>

              <Paragraph
                size='$4'
                textAlign='right'
                color='$grey7'
                maxWidth={348}
              >
                They may contact you about this event and other community
                initiatives.
              </Paragraph>

              <Paragraph
                size='$4'
                textAlign='center'
                color='$grey7'
                maxWidth={348}
              >
                You can tune your notifications in your{' '}
                <Link
                  color='$darkBlue7'
                  size='$4'
                  href={`/${queryData.viewer?.slugId}/settings/notifications`}
                  target='_blank'
                >
                  Settings
                </Link>
                .
              </Paragraph>
            </>
          ) : null}
        </YStack>

        <YStack justifyContent='center'>
          <Button onPress={attendEventAction}>
            <Button.Text>
              {data.eventTicketingTiers.edges.length > 0
                ? 'Yes, Proceed to Checkout'
                : 'Sounds good'}
            </Button.Text>
          </Button>
        </YStack>
      </XStack>
    </YStack>
  )
}
